import { useState } from "react";
import { Sparkle, X, Info, TrendUp } from "@phosphor-icons/react";

/**
 * RentEstimateChip — renders a unit's asking rent with a visual tell for
 * whether it's a HUMAN-set value or an AI-estimated one. AI values render
 * in italics with a small sparkle so it's obvious this is a soft number.
 * Clicking an AI-sourced rent opens an insight card explaining:
 *  · Range low / high from the same estimate
 *  · Model confidence (low / medium / high)
 *  · The Claude rationale (2–3 sentences)
 *  · Neighborhood context + when the estimate was pulled
 *
 * Props (all optional except rentCents):
 *   rentCents           number  – primary display value in cents
 *   source              string  – "claude_sonnet_4_5" flags as AI-estimated
 *   rationale           string  – Claude's 2–3 sentence explanation
 *   rangeLowCents       number  – low end of the AI range
 *   rangeHighCents      number  – high end of the AI range
 *   confidence          "low"|"medium"|"high"
 *   neighborhood        string
 *   updatedAt           ISO string – when the estimate was pulled
 *   size                "sm"|"md" – controls chip typography
 *   variant             "chip"|"inline" – full pill vs inline text
 */
export default function RentEstimateChip({
  rentCents, source, rationale, rangeLowCents, rangeHighCents,
  confidence, neighborhood, updatedAt, size = "md", variant = "chip",
}) {
  const [open, setOpen] = useState(false);
  if (!rentCents) return null;
  const isAI = source === "claude_sonnet_4_5";
  const display = `$${(rentCents / 100).toLocaleString()}`;
  const conf = (confidence || "medium").toLowerCase();
  const confColor = conf === "high" ? "text-emerald-700 bg-emerald-100"
                  : conf === "low"  ? "text-amber-700 bg-amber-100"
                  : "text-cyan-700 bg-cyan-100";

  const clickable = isAI && (rationale || rangeLowCents || rangeHighCents);

  const chipCls = variant === "chip"
    ? `inline-flex items-center gap-1 font-black rounded-full ${size === "sm" ? "px-2 py-0.5 text-[10px]" : "px-2.5 py-1 text-xs"} ${isAI ? "bg-cyan-50 text-cyan-800 border border-cyan-200 italic" : "bg-emerald-50 text-emerald-800 border border-emerald-200"} ${clickable ? "cursor-pointer hover:bg-cyan-100 transition-colors" : ""}`
    : `${isAI ? "italic text-cyan-800" : "text-emerald-800"} font-black ${clickable ? "cursor-pointer hover:underline" : ""}`;

  return (
    <>
      <span className={chipCls}
        onClick={clickable ? (e) => { e.preventDefault(); e.stopPropagation(); setOpen(true); } : undefined}
        title={isAI ? "AI-estimated · tap for insight" : "Set manually"}
        data-testid="rent-estimate-chip">
        {isAI && <Sparkle weight="fill" size={size === "sm" ? 10 : 12}/>}
        <span>{display}<span className="font-normal opacity-70">/mo</span></span>
        {isAI && <span className="text-[8px] uppercase tracking-widest opacity-70 not-italic">est</span>}
      </span>

      {open && (
        <div className="fixed inset-0 z-[500] bg-black/70 flex items-center justify-center p-4"
             onClick={() => setOpen(false)} data-testid="rent-insight-modal">
          <div className="bg-white w-full max-w-md rounded-2xl overflow-hidden shadow-2xl"
               onClick={e => e.stopPropagation()}>
            <div className="p-4 border-b-2 border-slate-200 flex items-start gap-2">
              <div className="w-9 h-9 rounded-full bg-cyan-100 text-cyan-700 flex items-center justify-center flex-shrink-0">
                <Sparkle weight="fill" size={16}/>
              </div>
              <div className="min-w-0 flex-1">
                <div className="text-[10px] font-black uppercase tracking-widest text-cyan-700">AI Rent Estimate</div>
                <div className="font-display font-black text-2xl text-[#0F1F3A] italic mt-0.5">{display}<span className="text-sm font-normal opacity-70 not-italic">/mo</span></div>
                {(rangeLowCents || rangeHighCents) && (
                  <div className="text-xs text-slate-600 mt-0.5">
                    Range ${(rangeLowCents/100 || 0).toLocaleString()} – ${(rangeHighCents/100 || 0).toLocaleString()}
                  </div>
                )}
              </div>
              <button onClick={() => setOpen(false)} data-testid="rent-insight-close"
                className="h-8 w-8 flex items-center justify-center hover:bg-slate-100 rounded"><X size={18}/></button>
            </div>

            <div className="p-4 space-y-3">
              <div className="flex items-center gap-2">
                <span className={`px-2 py-0.5 rounded-full text-[10px] font-black uppercase tracking-widest ${confColor}`}>
                  {conf} confidence
                </span>
                {neighborhood && (
                  <span className="text-[10px] font-black uppercase tracking-widest text-slate-500">
                    {neighborhood}
                  </span>
                )}
              </div>

              {rationale && (
                <div className="bg-slate-50 border-l-4 border-cyan-500 p-3">
                  <div className="text-[10px] font-black uppercase tracking-widest text-cyan-700 flex items-center gap-1 mb-1.5">
                    <Info size={11} weight="bold"/> How the model got there
                  </div>
                  <div className="text-sm text-slate-800 leading-relaxed">{rationale}</div>
                </div>
              )}

              <div className="text-[10px] text-slate-500 leading-relaxed pt-2 border-t border-slate-100">
                <div className="font-black uppercase tracking-widest text-slate-600 mb-1 flex items-center gap-1">
                  <TrendUp size={11} weight="bold"/> Sources
                </div>
                Powered by Claude Sonnet 4.5 with knowledge of Craigslist / Zillow / Redfin / RentCafe comps
                for {neighborhood ? `${neighborhood}, ` : ""}Philadelphia. This is a soft estimate — verify
                with local comps before signing a lease.
                {updatedAt && (
                  <div className="mt-1 opacity-70">
                    Pulled {new Date(updatedAt).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" })}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
